import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, List, ListItem, Text} from '@chakra-ui/react'
import Field from '../field'
import useAutocompleteAddress from '../forms/useAutocompleteAddress'
import useEffectEvent from '../../hooks/use-effect-event'

export default function Address1AutocompleteInput({
    form,
    prefix = '',
    fieldProps,
    rules,
    analyticsEvent,
    productItems
}) {
    const inputRef = useRef(null)
    const {suggestions, onPlacePrediction} = useAutocompleteAddress(form, prefix)
    const [open, setOpen] = useState(false)

    const pick = useEffectEvent(async (prediction) => {
        if (!prediction) return

        const main = prediction?.mainText?.text || ''
        const secondary = prediction?.secondaryText?.text || ''
        const label = secondary ? `${main}, ${secondary}` : main

        form.setValue(`${prefix}address1`, label, {shouldValidate: true, shouldDirty: true})

        await onPlacePrediction(prediction)

        setOpen(false)
        inputRef.current?.focus?.()
    })

    const dropdown = () => {
        if (!open || !suggestions?.length) return null

        return (
            <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={20}
                mt={1}
                borderWidth="1px"
                borderRadius="md"
                bg="white"
                maxH="260px"
                overflow="auto"
            >
                <List spacing={0}>
                    {suggestions.map((s, idx) => {
                        const p = s.placePrediction
                        const main = p?.mainText?.text || ''
                        const secondary = p?.secondaryText?.text || ''
                        return (
                            <ListItem
                                key={idx}
                                px={3}
                                py={2}
                                cursor="pointer"
                                _hover={{bg: 'gray.50'}}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => pick(p)}
                            >
                                <Text fontWeight="semibold">{main}</Text>
                                {secondary ? <Text fontSize="sm">{secondary}</Text> : null}
                            </ListItem>
                        )
                    })}
                </List>
            </Box>
        )
    }

    return (
        <Box position="relative">
            <Field
                {...fieldProps}
                type="text"
                ref={inputRef}
                rules={rules ? rules : fieldProps.rules}
                inputProps={{
                    ...fieldProps.inputProps,
                    onFocus: (e) => {
                        fieldProps?.inputProps?.onFocus?.(e)
                        if (suggestions?.length) setOpen(true)
                    },
                    onBlur: (e) => {
                        fieldProps?.inputProps?.onBlur?.(e)
                        setTimeout(() => setOpen(false), 150)
                    }
                }}
                analyticsEvent={analyticsEvent}
                productItems={productItems}
            />

            {dropdown()}
        </Box>
    )
}

Address1AutocompleteInput.propTypes = {
    form: PropTypes.object.isRequired,
    prefix: PropTypes.string,
    fieldProps: PropTypes.object.isRequired,
    rules: PropTypes.object,
    analyticsEvent: PropTypes.string,
    productItems: PropTypes.array
}
